import {
  Search, X, Landmark, Home, CheckCircle2, AlertTriangle,
  ShieldCheck, Pencil, Trash2, Columns3,
} from "lucide-react";
import { useState } from "react";
import { SortIcon, HoverPillButton } from "@/components/condomini/condominiHelpers";
import { formatQuota, DirittoBadge } from "@/components/CatastoPanel";
import { VisuraSoggettoButton } from "@/components/VisuraSoggettoButton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";

// Colonne opzionali dell'anagrafica: l'amministratore sceglie quali vedere (Nominativo e Azioni sempre visibili).
const OPTIONAL_COLUMNS = [
  { key: "ruolo", label: "Ruolo" },
  { key: "interno", label: "Interno" },
  { key: "millesimi", label: "Millesimi" },
  { key: "codice_fiscale", label: "Codice Fiscale" },
  { key: "email", label: "Email" },
  { key: "telefono", label: "Telefono" },
];
const COLS_STORAGE_KEY = "anagrafica_visible_cols";

const loadVisibleCols = () => {
  try {
    const raw = localStorage.getItem(COLS_STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set(OPTIONAL_COLUMNS.map((c) => c.key)); // default: tutte visibili
};

export const ResidentsTable = ({
  residents, displayed, resSearch, setResSearch, resQuery,
  sortKey, sortDir, toggleSort, setSortKey, move, shadeForResident, selected,
  onPortal, onEdit, onDelete,
}) => {
  const [visibleCols, setVisibleCols] = useState(loadVisibleCols);
  const isVisible = (key) => visibleCols.has(key);
  const toggleCol = (key) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  if (residents.length === 0) {
    return <div className="p-8 text-center text-muted-foreground text-sm">Nessun condòmino registrato. Usa "Aggiungi condòmino" per inserirli manualmente.</div>;
  }
  return (
    <>
      <div className="px-6 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="resident-search-input"
            type="text"
            value={resSearch}
            onChange={(e) => setResSearch(e.target.value)}
            placeholder="Cerca condòmino per nome, C.F., interno, email o telefono…"
            className="w-full h-9 pl-9 pr-9 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40 focus:border-[#007BFF]"
          />
          {resSearch && (
            <button data-testid="resident-search-clear" onClick={() => setResSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title="Pulisci ricerca">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {resQuery && (
          <span data-testid="resident-search-count" className="text-xs text-muted-foreground whitespace-nowrap">
            {displayed.length} di {residents.length}
          </span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="columns-selector-btn" title="Scegli le colonne da mostrare"
              className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-background text-sm font-medium text-muted-foreground hover:text-foreground hover:border-[#007BFF]/50 transition-colors">
              <Columns3 className="h-4 w-4" />
              <span className="hidden sm:inline">Colonne</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Colonne da mostrare</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {OPTIONAL_COLUMNS.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                data-testid={`column-toggle-${c.key}`}
                checked={isVisible(c.key)}
                onCheckedChange={() => toggleCol(c.key)}
                onSelect={(e) => e.preventDefault()}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {displayed.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="resident-search-empty">
          Nessun condòmino corrisponde a «{resSearch}».
        </div>
      ) : (
        <div className="overflow-x-auto scroll-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-semibold min-w-[230px]">
                  <button data-testid="sort-nome" onClick={() => toggleSort("nome")} className="inline-flex items-center gap-1 hover:text-foreground transition-colors duration-150 uppercase">
                    Nominativo <SortIcon active={sortKey === "nome"} dir={sortDir} />
                  </button>
                </th>
                {isVisible("ruolo") && <th className="px-5 py-3 font-semibold">Ruolo</th>}
                {isVisible("interno") && <th className="px-5 py-3 font-semibold">Int.</th>}
                {isVisible("millesimi") && <th className="px-5 py-3 font-semibold">Millesimi</th>}
                {isVisible("codice_fiscale") && <th className="px-5 py-3 font-semibold">Codice Fiscale</th>}
                {isVisible("email") && <th className="px-5 py-3 font-semibold">Email</th>}
                {isVisible("telefono") && <th className="px-5 py-3 font-semibold">Telefono</th>}
                <th className="px-5 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((r, i) => (
                <tr key={r.id} data-testid={`resident-row-${i}`} className={`list-enter border-b border-border last:border-0 ${shadeForResident(r)} hover:bg-[#007BFF]/[0.1] transition-colors duration-150`} style={{ animationDelay: `${Math.min(i, 15) * 18}ms` }}>
                  <td className="px-5 py-3.5 font-medium">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{r.nome}</span>
                      {r.catasto_source === "comproprietario" && (
                        <span data-testid={`resident-catasto-tag-${i}`} title="Nominativo aggiunto automaticamente dal Catasto come comproprietario"
                          className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200">
                          <Landmark className="h-2.5 w-2.5" />da catasto
                        </span>
                      )}
                    </div>
                    {Array.isArray(r.catasto_immobili) && r.catasto_immobili.length > 0 && (
                      <div className="mt-1 flex flex-col gap-0.5" data-testid={`resident-catasto-${i}`}>
                        {r.catasto_immobili.map((u, ui) => (
                          <span key={ui} className="inline-flex items-center gap-1 text-[10px] leading-tight font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 w-fit whitespace-nowrap">
                            <Home className="h-3 w-3 shrink-0" />
                            Fg. {u.foglio || "—"} · Part. {u.particella || "—"}{u.subalterno ? ` · Sub. ${u.subalterno}` : ""}{u.categoria ? ` · ${u.categoria}` : ""}{u.titolarita ? ` · ${formatQuota(u.titolarita)}` : ""}
                            <DirittoBadge titolarita={u.titolarita} className="ml-1" />
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  {isVisible("ruolo") && (
                  <td className="px-5 py-3.5">
                    {r.ruolo === "proprietario" ? (
                      <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">Proprietario</span>
                    ) : r.ruolo === "inquilino" ? (
                      <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">Inquilino</span>
                    ) : (<span className="text-muted-foreground">—</span>)}
                  </td>
                  )}
                  {isVisible("interno") && <td className="px-5 py-3.5 text-muted-foreground">{r.interno || "—"}</td>}
                  {isVisible("millesimi") && <td className="px-5 py-3.5"><span className="font-semibold text-[#007BFF]">{r.millesimi || "—"}</span></td>}
                  {isVisible("codice_fiscale") && (
                  <td className="px-5 py-3.5">
                    {(r.codice_fiscale || "").trim() ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-muted-foreground">{r.codice_fiscale}</span>
                        {r.cf_source === "verificato" && (
                          <span data-testid={`cf-verificato-${i}`} className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 w-fit">
                            <CheckCircle2 className="h-2.5 w-2.5" />CF verificato
                          </span>
                        )}
                        {r.cf_source === "calcolato" && (
                          <span data-testid={`cf-calcolato-${i}`} title="Codice fiscale calcolato dai dati catastali: controllalo e conferma" className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 w-fit">
                            <AlertTriangle className="h-2.5 w-2.5" />CF calcolato · da verificare
                          </span>
                        )}
                      </div>
                    ) : (
                      <span data-testid={`resident-no-cf-${i}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" title="Codice fiscale mancante: richiesto per la sincronizzazione catastale">
                        <AlertTriangle className="h-3 w-3" />Manca C.F.
                      </span>
                    )}
                  </td>
                  )}
                  {isVisible("email") && (
                  <td className="px-5 py-3.5 text-muted-foreground text-xs">
                    {r.email ? <a href={`mailto:${r.email}`} className="hover:text-[#007BFF] break-all">{r.email}</a> : "—"}
                  </td>
                  )}
                  {isVisible("telefono") && (
                  <td className="px-5 py-3.5 text-muted-foreground text-xs whitespace-nowrap">
                    {r.telefono ? <a href={`tel:${r.telefono}`} className="hover:text-[#007BFF]">{r.telefono}</a> : "—"}
                  </td>
                  )}
                  <td className="px-3 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <HoverPillButton data-testid={`portal-resident-${i}`}
                        title={r.has_portal ? "Portale attivo — gestisci" : "Crea portale di accesso"}
                        onClick={() => onPortal(r)}
                        icon={ShieldCheck}
                        label={r.has_portal ? "Portale attivo" : "Crea portale"}
                        hover="emerald"
                        className={r.has_portal ? "text-emerald-600" : "text-muted-foreground"} />
                      <VisuraSoggettoButton resident={r} building={selected} index={i} />
                      <HoverPillButton data-testid={`edit-resident-${i}`} title="Modifica" onClick={() => onEdit(r)}
                        icon={Pencil} label="Modifica" hover="blue" className="text-muted-foreground" />
                      <HoverPillButton data-testid={`delete-resident-${i}`} title="Elimina" onClick={() => onDelete(r)}
                        icon={Trash2} label="Elimina" hover="red" className="text-muted-foreground" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
